import React from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';

import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function NexusRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, content_alignment, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'NexusRendering');

  const textAlignmentClass =
    content_alignment === 'Left' ? 'text-left' :
    content_alignment === 'Right' ? 'text-right' :
    'text-center';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`bg-[#F8F8F8] px-16 md:px-90 2xl:px-208 pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {title && (
          <Typography
            as="h3"
            className={`mb-36 md:mb-48 text-32 xl:text-36 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909] ${textAlignmentClass}`}
          >
            {title}
          </Typography>
        )}
        {description && (
          <div
            className={`mb-24 text-16 md:text-18 2xl:text-20 font-normal leading-6 text-[#333] ${textAlignmentClass}`}
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
          />
        )}
        {cards && cards.length > 0 && (
          // Responsive layout is CSS-driven (flex-col on mobile, flex-row at lg) instead of a
          // useMediaQuery gate — so ALL content (incl. each card's description) is server-rendered.
          // Both the mobile (title-only) and desktop (title + description) variants are in the SSR
          // HTML, toggled purely by CSS, avoiding the previous client-only description + hydration swap.
          <div className="flex flex-col justify-evenly gap-30 text-center md:gap-20 lg:flex-row xl:gap-47 2xl:gap-47">
            {cards.map((card: MediaCard, index: number) => {
              const cardImage = card?.desktop_image;
              return (
                <div
                  key={index}
                  className="flex flex-row lg:w-[40%] lg:flex-col middleresolution:w-[30%] xl:w-[30%] 2xl:w-[30%]"
                >
                  {cardImage?.url && (
                    <img className="block w-120 md:w-180 lg:m-auto" src={cardImage.url} alt={cardImage?.description ?? ''} />
                  )}
                  {/* Desktop (lg+): title + description — present in the SSR HTML, hidden on mobile via CSS. */}
                  <div className="hidden lg:block">
                    {card?.title && (
                      <Typography
                        as="h3"
                        className="text-20 font-bold leading-10 text-primary-black lg:text-20 xl:text-20 2xl:text-22"
                      >
                        {card.title}
                      </Typography>
                    )}
                    {card?.description && (
                      <div
                        className="text-16 font-normal leading-[1.5rem] text-primary-black md:text-14"
                        dangerouslySetInnerHTML={{ __html: sanitize(card.description, { ADD_ATTR: ['target'] }) }}
                      />
                    )}
                  </div>
                  {/* Mobile (< lg): title only. */}
                  {card?.title && (
                    <Typography
                      as="h3"
                      className="m-auto block text-20 font-bold leading-10 text-primary-black md:text-30 lg:hidden lg:leading-[3.5rem] 2xl:text-22"
                    >
                      {card.title}
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
